import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { obterOuCriarSessionId } from '@/shared/lib/jukeboxSession';
import { futebolRepository } from '../../data/futebolRepository';
import type { FutebolTermometroContagem } from '@/shared/types';

export type TipoVoto = 'apoiar' | 'secar';
export type TimeVoto = 'casa' | 'visitante';

export interface TermometroClique {
  tipo: TipoVoto;
  time: TimeVoto;
}

export interface UseTermometroEmissorReturn {
  enviarClique: (tipo: TipoVoto, time: TimeVoto) => Promise<string | null>;
}

/**
 * useTermometroEmissor — envia cliques via RPC `incrementar_termometro`
 * (SECURITY DEFINER, valida tela_id/fixture_id no servidor). Um clique por
 * sessão. Debounce de 100 ms evita flood de chamadas em cliques muito rápidos.
 * Sem tela/fixture devolve null. Erro da RPC rejeita a promise — o chamador trata.
 */
export function useTermometroEmissor(telaId: string | null, fixtureId: number | null): UseTermometroEmissorReturn {
  const lastSentRef = useRef<number>(0);

  const enviarClique = useCallback(async (tipo: TipoVoto, time: TimeVoto): Promise<string | null> => {
    if (!telaId || !fixtureId) return null;
    const agora = Date.now();
    if (agora - lastSentRef.current < 100) return null;
    lastSentRef.current = agora;
    return futebolRepository.incrementarTermometro({
      tela_id: telaId,
      fixture_id: fixtureId,
      time,
      tipo,
      session_id: obterOuCriarSessionId(telaId),
    });
  }, [telaId, fixtureId]);

  return { enviarClique };
}

export interface TermometroTotais {
  apoiar: number;
  secar: number;
}

const chaveContagem = (row: FutebolTermometroContagem) => `${row.time}|${row.tipo}`;

const somarTotais = (linhas: Map<string, FutebolTermometroContagem>): TermometroTotais => {
  let apoiar = 0;
  let secar = 0;
  linhas.forEach(row => {
    if (row.tipo === 'apoiar') apoiar += row.contagem;
    else                       secar  += row.contagem;
  });
  return { apoiar, secar };
};

/**
 * useTermometroReceptor — assina `postgres_changes` em
 * `futebol_termometro_contagem` filtrado por tela_id e repassa cada
 * atualização de contador como um "clique" (tipo/time) para quem consome —
 * mantém a UI de partículas/HUD sem mudar de formato.
 *
 * Os totais vêm da coluna `contagem` (absoluta) e são hidratados a cada
 * `SUBSCRIBED`, não acumulados no cliente: a TV fica 24/7 no ar e um CDC
 * perdido no buraco do socket deixaria o cabo de guerra errado até o overlay
 * desmontar — o heartbeat do worker religa o socket, mas não repõe o evento.
 */
export function useTermometroReceptor(
  telaId: string | null,
  fixtureId: number | null,
  onCliqueHandler: (clique: TermometroClique) => void,
): TermometroTotais {
  const cbRef = useRef(onCliqueHandler);
  useEffect(() => { cbRef.current = onCliqueHandler; });

  const [totais, setTotais] = useState<TermometroTotais>({ apoiar: 0, secar: 0 });
  const linhasRef = useRef<Map<string, FutebolTermometroContagem>>(new Map());

  useEffect(() => {
    if (!telaId || !fixtureId) return;

    let ativo = true;
    linhasRef.current = new Map();
    setTotais({ apoiar: 0, secar: 0 });

    const hidratar = async () => {
      try {
        const linhas = await futebolRepository.buscarContagemTermometro(telaId, fixtureId);
        if (!ativo) return;
        linhasRef.current = new Map(linhas.map(row => [chaveContagem(row), row]));
        setTotais(somarTotais(linhasRef.current));
      } catch {
        // Falha de rede não apaga o total que já está na tela.
      }
    };

    const aplicarLinha = (row: FutebolTermometroContagem) => {
      linhasRef.current.set(chaveContagem(row), row);
      setTotais(somarTotais(linhasRef.current));
    };

    const canal = supabase
      .channel(`termometro-${telaId}-${fixtureId}`)
      .on(
        'postgres_changes' as never,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'futebol_termometro_contagem',
          filter: `tela_id=eq.${telaId}`,
        } as never,
        (payload: { new: FutebolTermometroContagem }) => {
          const row = payload.new;
          if (row.fixture_id !== fixtureId) return;
          aplicarLinha(row);
          cbRef.current({ tipo: row.tipo, time: row.time });
        },
      )
      .on(
        'postgres_changes' as never,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'futebol_termometro_contagem',
          filter: `tela_id=eq.${telaId}`,
        } as never,
        (payload: { new: FutebolTermometroContagem }) => {
          const row = payload.new;
          if (row.fixture_id !== fixtureId) return;
          aplicarLinha(row);
          cbRef.current({ tipo: row.tipo, time: row.time });
        },
      )
      .subscribe(status => {
        if (status === 'SUBSCRIBED') void hidratar();
      });

    return () => {
      ativo = false;
      supabase.removeChannel(canal);
    };
  }, [telaId, fixtureId]);

  return totais;
}
